/**
 * Settings Page Component
 *
 * Main settings page for user preferences and configuration.
 * Currently includes digest notification preferences for coaches.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Header, DashboardLayout } from '@/components/shared';
import DigestPreferences from './DigestPreferences';
import SessionLoggingSettings from './SessionLoggingSettings';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';
import { useModalSuccess } from '@/contexts/ModalContext';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Get user info from localStorage and parse the JSON
  const userInfoStr = localStorage.getItem('user_info');
  const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
  const userRole = userInfo?.role;
  const isCoach = userRole === 'coach' || userRole === 'admin';

  // State for user's teams
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);

  /**
   * Fetch user's teams on component mount
   */
  useEffect(() => {
    if (isCoach) {
      fetchUserTeams();
    }
  }, [isCoach]);

  useModalSuccess(() => { if (isCoach) fetchUserTeams(); });

  /**
   * Fetch teams that the coach is a member of
   */
  const fetchUserTeams = async () => {
    try {
      const result = await authenticatedFetch(
        getApiUrl('/api/teams/teams/'),
        { method: 'GET' },
        'fetching user teams'
      );

      if (result.success && result.data) {
        // Handle custom pagination format {pagination: {...}, data: [...]}
        if (result.data.data && Array.isArray(result.data.data)) {
          setUserTeams(result.data.data);
        }
        // Handle DRF pagination format {results: [...]}
        else if (result.data.results && Array.isArray(result.data.results)) {
          setUserTeams(result.data.results);
        }
        // Handle direct array response
        else if (Array.isArray(result.data)) {
          setUserTeams(result.data);
        }
        // Fallback to empty array for any other structure (prevents crash)
        else {
          console.warn('Unexpected teams response format:', result.data);
          setUserTeams([]);
        }
      } else {
        // Explicitly set empty array on error
        setUserTeams([]);
        console.error('Error fetching teams:', result.error);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  return (
    <div className="min-h-screen cgm-bg-primary">
      {/* Header */}
      <Header
        userInfo={userInfo}
        isDark={isDark}
      />

      {/* Main Content */}
      <DashboardLayout>
        <div className="space-y-6">
          {/* Session Logging Settings Section (Coaches Only) */}
          {isCoach && (
            <section className={`cgm-card rounded-2xl p-6`}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold cgm-text-primary">
                  Session Logging Settings
                </h2>
                <p className="mt-2 cgm-text-secondary">
                  Configure when and who receives session logging reminders
                </p>
              </div>
              
              {loadingTeams ? (
                <div className="py-8 text-center cgm-text-secondary">
                  Loading teams...
                </div>
              ) : userTeams.length === 0 ? (
                <div className="py-8 text-center cgm-text-secondary">
                  No teams found. Create a team to configure session logging.
                </div>
              ) : (
                <div className="space-y-6">
                  {userTeams.map((team) => (
                    <SessionLoggingSettings
                      key={team.id}
                      teamId={team.id}
                      teamName={team.name}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Digest Notifications Section (Coaches Only) */}
          {isCoach && (
            <section className={`cgm-card rounded-2xl p-6`}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold cgm-text-primary">
                  Digest Notifications
                </h2>
                <p className="mt-2 cgm-text-secondary">
                  Configure when and how you receive email digest updates
                </p>
              </div>
              <DigestPreferences />
            </section>
          )}

          {/* Non-coach Message */}
          {!isCoach && (
            <div className="cgm-stat-info rounded-2xl p-6 border border-blue-200 dark:border-blue-800/30">
              <div className="flex">
                <div className="shrink-0">
                  <svg
                    className="h-5 w-5 cgm-icon-data"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium cgm-text-primary">
                    Coach Features Only
                  </h3>
                  <div className="mt-2 text-sm cgm-text-secondary">
                    <p>
                      Digest notifications and other advanced settings are available
                      for coaches. If you need access to these features, please contact
                      your administrator.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Future Settings Sections */}
          {/* Uncomment and expand as needed
          <section>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Account Settings
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Manage your account information and preferences
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-500">Account settings coming soon...</p>
            </div>
          </section>
          */}
        </div>
      </DashboardLayout>
    </div>
  );
};

export default SettingsPage;
